// Angular imports
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

// CC-Common imports
import {
    CCTimeZoneService,
    UtcToUserTimezonePipe,
    TimezoneConstants,
    IconConstants,
    CCLocalStorageService,
    CCLocalStorageCategory,
    Utils
} from 'cc-common';

// Service imports
import { ScreenService } from '../../_services/shared-services/screen.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { PrecisionService } from '../../_services/shared-services/precision.service';
import { UserBackgroundService } from '../../_services/shared-services/user-background.service';
import { UserService } from '../../_services/data-services/user.service';

// Environment imports
import { environment } from '../../../environments/environment';

// Model imports
import { KeyValueResultDto } from '../../_models/key-value-result-dto';

// Constant imports
import { StorageKeyConstants } from '../../_constants/storage-key-constants';
import { ThemeColor, themeColors } from '../../_constants/theme-colors';

interface ITextValue {
    text: string;
    value: string;
}

@Component({
    selector: 'app-preferences',
    templateUrl: './preferences.component.html',
    styleUrls: ['./preferences.component.scss'],
    providers: [UtcToUserTimezonePipe]
})
export class PreferencesComponent implements OnInit {
    @Input() visible = false;
    @Output() onClose = new EventEmitter<boolean>();

    isSmallScreen = false;

    themeColors: ThemeColor[];
    timeZones: string[];
    openLinksOptions: ITextValue[];
    scrollbarOptions: KeyValueResultDto[];
    scrollbarTypeOptions: KeyValueResultDto[];
    topMenuOptions: ITextValue[];
    ciraMailNotesOptions: ITextValue[];

    selectedThemeColor: string;
    selectedTimeZoneName: string;
    selectedPrecision: number;
    selectedOpenLinks: string;
    timezoneConstant: any[];
    selectedScrollOption: string;
    selectedScrollTypeOption: string;
    selectedTopMenuOption: string;

    currentScrollOption: string;
    currentScrollTypeOption: string;

    cancelButtonIcon = IconConstants.CANCEL;
    saveButtonIcon = IconConstants.SAVE;
    formLabelLocation = 'top';

    constructor(
        private readonly screenService: ScreenService,
        private readonly helperService: HelperService,
        private readonly userBackgroundService: UserBackgroundService,
        private readonly precisionService: PrecisionService,
        private readonly ccTimezoneService: CCTimeZoneService,
        private readonly userService: UserService,
        private readonly localStorageService: CCLocalStorageService
    ) {
        this.themeColors = themeColors;
        this.timezoneConstant = TimezoneConstants.abbreviations;
        this.timezoneConstant.forEach((r) => {
            r.displayText = `${r.text.split(' ')[0]} ${r.value}`;
        });
        this.onCancelClick = this.onCancelClick.bind(this);
        this.onApplyClick = this.onApplyClick.bind(this);
    }

    ngOnInit() {
        this.isSmallScreen = this.screenService.isSmallScreen();
        this.screenService.changed.subscribe(() => (this.isSmallScreen = this.screenService.isSmallScreen()));
    }

    show() {
        this.visible = true;
    }

    onShown(e: any): void {
        this.loadPreferencesEditor();
        e.component.repaint();
    }

    onHidden(): void {
        this.closePreference();
    }

    onApplyClick() {
        let requireRefresh = false;
        const previousThemeColor = this.localStorageService.getItem(
            StorageKeyConstants.userThemeKey,
            CCLocalStorageCategory.PREF
        );
        // Theme
        this.localStorageService.setItem(
            StorageKeyConstants.userThemeKey,
            this.selectedThemeColor,
            CCLocalStorageCategory.PREF
        );
        // If change in Theme Color
        if (Utils.notEqualsIgnoreCase(this.selectedThemeColor, previousThemeColor)) {
            this.userService.updateInThemeColor(true);
        }

        // Timezone
        if (this.selectedTimeZoneName) {
            const timezoneToStore = this.getStorableTimezoneName(this.selectedTimeZoneName);
            this.localStorageService.setItem(
                StorageKeyConstants.userTimeZoneKey,
                timezoneToStore.toString(),
                CCLocalStorageCategory.PREF
            );
        }

        // Rounding
        if (this.selectedPrecision !== null) {
            this.localStorageService.setItem(
                StorageKeyConstants.userPrecisionKey,
                this.selectedPrecision.toString(),
                CCLocalStorageCategory.PREF
            );
        }

        // Open Links
        if (this.selectedOpenLinks !== null) {
            this.localStorageService.setItem(
                StorageKeyConstants.userOpenLinksKey,
                this.selectedOpenLinks,
                CCLocalStorageCategory.PREF
            );
        }

        // Scrollbar
        if (this.selectedScrollOption !== null) {
            requireRefresh = this.currentScrollOption !== this.selectedScrollOption;
            this.localStorageService.setItem(
                StorageKeyConstants.userScrollbarKey,
                this.selectedScrollOption,
                CCLocalStorageCategory.PREF
            );
        }

        // Scrollbar Type
        if (this.selectedScrollTypeOption !== null) {
            requireRefresh = this.currentScrollTypeOption !== this.selectedScrollTypeOption;
            this.localStorageService.setItem(
                StorageKeyConstants.userScrollTypeKey,
                this.selectedScrollTypeOption,
                CCLocalStorageCategory.PREF
            );
        }

        // Top Menu
        if (this.selectedTopMenuOption !== null) {
            this.localStorageService.setItem(
                StorageKeyConstants.userTopMenuKey,
                this.selectedTopMenuOption,
                CCLocalStorageCategory.PREF
            );
        }

        // Hide Preferences
        this.visible = false;
        this.userService.updateUserPreferences(true);
        this.userBackgroundService.logActivity(`save_preferences`, 'SavePreferences', new Date(), 'success');

        if (!!requireRefresh) {
            const dialog = this.helperService.getConfirmationDialog(
                'Confirmation',
                'Preference changes requires browser refresh. Do you want to refresh now?'
            );
            dialog.show().then((answer: boolean) => {
                if (answer === true) {
                    location.reload();
                } else {
                    this.onClose.emit(requireRefresh);
                }
            });
        }
    }

    onCancelClick() {
        this.closePreference();
    }

    private loadPreferencesEditor() {
        // Theme
        this.selectedThemeColor = this.localStorageService.getItem(
            StorageKeyConstants.userThemeKey,
            CCLocalStorageCategory.PREF
        );

        if (!this.selectedThemeColor) {
            this.localStorageService.setItem(
                StorageKeyConstants.userThemeKey,
                environment.defaultThemeColor,
                CCLocalStorageCategory.PREF
            );
            this.selectedThemeColor = environment.defaultThemeColor;
        }

        // Timezone
        const currentTimeZone = this.helperService.getUserTimezone();
        if (currentTimeZone) {
            this.selectedTimeZoneName = this.getSelectableTimezoneName(currentTimeZone);
        } else {
            this.ccTimezoneService.getLocalTimezone().subscribe((data) => {
                this.selectedTimeZoneName = this.getSelectableTimezoneName(data);
            });
        }
        this.timeZones = this.timezoneConstant.map((r) => r.displayText);

        // Rounding
        const precision = this.precisionService.getPrecision();
        if (!!precision && Number.isInteger(precision) && precision >= 0 && precision <= 4) {
            this.selectedPrecision = precision;
        } else {
            this.selectedPrecision = environment.defaultRounding;
        }

        // Open Links
        this.openLinksOptions = [
            { text: 'Open in same window', value: 'internal' },
            { text: 'Open in new window', value: 'external' }
        ];
        this.selectedOpenLinks =
            this.localStorageService.getItem(StorageKeyConstants.userOpenLinksKey, CCLocalStorageCategory.PREF) ||
            'internal';

        // scrollbar options
        this.scrollbarOptions = [
            {
                displayText: 'Always',
                key: 'always',
                isDefault: true
            },
            { displayText: 'On Scroll', key: 'onScroll', isDefault: false },
            { displayText: 'On Hover', key: 'onHover', isDefault: false }
        ];
        this.currentScrollOption =
            this.localStorageService.getItem(StorageKeyConstants.userScrollbarKey, CCLocalStorageCategory.PREF) ||
            'always';
        this.selectedScrollOption = this.currentScrollOption;

        // scrollbar type options
        this.scrollbarTypeOptions = [
            { displayText: 'Modern Look', key: 'modern', isDefault: true },
            { displayText: 'Classic Look', key: 'classic', isDefault: false }
        ];
        this.currentScrollTypeOption =
            this.localStorageService.getItem(StorageKeyConstants.userScrollTypeKey, CCLocalStorageCategory.PREF) ||
            'modern';
        this.selectedScrollTypeOption = this.currentScrollTypeOption;

        // Top Menu
        this.topMenuOptions = [
            { text: 'Yes', value: 'yes' },
            { text: 'No', value: 'no' }
        ];
        this.selectedTopMenuOption =
            this.localStorageService.getItem(StorageKeyConstants.userTopMenuKey, CCLocalStorageCategory.PREF) || 'yes';

        // By Default, v2 attachments will download
        this.localStorageService.setItem(StorageKeyConstants.userDownloadsKey, 'download', CCLocalStorageCategory.PREF);
    }

    private getSelectableTimezoneName(storedValue: string): string {
        const rec = this.timezoneConstant.find((r) => {
            return r.utc.indexOf(storedValue) !== -1;
        });
        return !!rec ? rec.displayText : storedValue;
    }

    private getStorableTimezoneName(selectedValue: string): string {
        const rec = this.timezoneConstant.find((r) => {
            return r.displayText === selectedValue;
        });
        return !!rec ? rec.utc[0] : selectedValue;
    }

    private closePreference() {
        this.visible = false;
        this.onClose.emit(false);
    }
}
